import { useEffect, useState } from "react";
import {
  decodeMetar,
  decodeTaf,
  formatObservedTime,
  type KlartextRow,
  type TafKlartext,
} from "@client/lib/aviationWeatherDecode";
import { useShowDataSources } from "../lib/dataSource";

type ViewMode = "klartext" | "raw";

// Auswahl bleibt während der Sitzung erhalten (modulweite Variable) — Parität
// zur nativen AirportWeatherSheet.
let sessionMode: ViewMode = "klartext";

interface AirportWeather {
  icao: string;
  metarRaw: string | null;
  tafRaw: string | null;
  observedAt: string | null;
  issuedAt: string | null;
}

interface AwcMetar {
  icaoId?: string;
  rawOb?: string;
  reportTime?: string;
}
interface AwcTaf {
  icaoId?: string;
  rawTAF?: string;
  issueTime?: string;
}

// Web nutzt denselben Proxy-Endpunkt wie nativ; relative URL, da der Express-
// Server das Web-Bundle ausliefert.
async function fetchAirportWeather(icao: string): Promise<AirportWeather> {
  const code = icao.trim().toUpperCase();
  const res = await fetch(`/api/proxy/metar?ids=${encodeURIComponent(code)}`);
  if (!res.ok) {
    throw new Error(`Wetterdienst nicht erreichbar (${res.status})`);
  }
  const data = (await res.json()) as { metars?: AwcMetar[]; tafs?: AwcTaf[] };
  const metars = Array.isArray(data.metars) ? data.metars : [];
  const tafs = Array.isArray(data.tafs) ? data.tafs : [];
  const metar = metars.find((m) => (m.icaoId || "").toUpperCase() === code) || metars[0];
  const taf = tafs.find((t) => (t.icaoId || "").toUpperCase() === code) || tafs[0];
  return {
    icao: code,
    metarRaw: metar?.rawOb?.trim() || null,
    tafRaw: taf?.rawTAF?.trim() || null,
    observedAt: metar?.reportTime || null,
    issuedAt: taf?.issueTime || null,
  };
}

interface AirportWeatherPanelProps {
  icao: string;
  name: string;
  onClose: () => void;
}

export default function AirportWeatherPanel({ icao, name, onClose }: AirportWeatherPanelProps) {
  const isAdmin = useShowDataSources();
  const [mode, setModeState] = useState<ViewMode>(sessionMode);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AirportWeather | null>(null);

  const setMode = (m: ViewMode) => {
    sessionMode = m;
    setModeState(m);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    fetchAirportWeather(icao)
      .then((w) => {
        if (!cancelled) setData(w);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Unbekannter Fehler");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [icao]);

  return (
    <div className="fixed inset-0 z-[1000] flex items-end justify-center sm:items-center p-3" data-testid="airport-weather-panel">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-sm max-h-[80vh] flex flex-col rounded-2xl border border-slate-200 bg-white shadow-xl">
        <div className="flex items-start gap-3 px-4 pt-4 pb-3 border-b border-slate-100">
          <div className="flex-1 min-w-0">
            <div className="text-base font-bold text-slate-800 truncate">{name}</div>
            <div className="text-xs font-semibold text-slate-500 mt-0.5">{icao} · Flugwetter</div>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 text-slate-400 hover:text-slate-600 text-lg leading-none px-1"
            aria-label="Schließen"
            data-testid="airport-weather-close"
          >
            ×
          </button>
        </div>

        <div className="flex gap-1 p-1 mx-4 mt-3 rounded-lg bg-slate-100">
          <button
            onClick={() => setMode("klartext")}
            className={`flex-1 py-1.5 rounded-md text-sm font-semibold ${
              mode === "klartext" ? "bg-brand-500 text-white" : "text-slate-600"
            }`}
            data-testid="airport-weather-toggle-klartext"
          >
            Klartext
          </button>
          <button
            onClick={() => setMode("raw")}
            className={`flex-1 py-1.5 rounded-md text-sm font-semibold ${
              mode === "raw" ? "bg-brand-500 text-white" : "text-slate-600"
            }`}
            data-testid="airport-weather-toggle-raw"
          >
            Roh
          </button>
        </div>

        {loading ? (
          <div className="px-4 py-8 text-center text-sm text-slate-500">Wetter wird geladen…</div>
        ) : error ? (
          <div className="px-4 py-8 text-center text-sm text-amber-700">{error}</div>
        ) : (
          <div className="overflow-y-auto px-4 py-3 space-y-4">
            <Section
              title="METAR (aktuell)"
              subtitle={formatObservedTime(data?.observedAt ?? null)}
              emptyText="Keine aktuelle METAR verfügbar"
              hasContent={!!data?.metarRaw}
            >
              {mode === "raw" ? <Raw raw={data?.metarRaw ?? null} /> : <MetarKlartext raw={data?.metarRaw ?? null} />}
            </Section>

            <Section
              title="TAF (Vorhersage)"
              subtitle={null}
              emptyText="Keine aktuelle TAF verfügbar"
              hasContent={!!data?.tafRaw}
            >
              {mode === "raw" ? <Raw raw={data?.tafRaw ?? null} /> : <TafKlartextView raw={data?.tafRaw ?? null} />}
            </Section>

            {isAdmin ? (
              <div className="text-[11px] text-slate-400 text-center pt-1">
                Quelle: NOAA / aviationweather.gov
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({
  title,
  subtitle,
  emptyText,
  hasContent,
  children,
}: {
  title: string;
  subtitle: string | null;
  emptyText: string;
  hasContent: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-xs font-bold uppercase tracking-wide text-brand-600">{title}</div>
        {subtitle ? <div className="text-[10px] font-semibold text-slate-400">{subtitle}</div> : null}
      </div>
      {hasContent ? children : <div className="text-xs text-slate-500">{emptyText}</div>}
    </div>
  );
}

function Raw({ raw }: { raw: string | null }) {
  if (!raw) return null;
  return <pre className="whitespace-pre-wrap break-words font-mono text-xs text-slate-600 leading-relaxed">{raw}</pre>;
}

function Rows({ rows }: { rows: KlartextRow[] }) {
  return (
    <div className="space-y-1">
      {rows.map((r, i) => (
        <div key={i} className="flex gap-2 items-start">
          <div className="w-28 shrink-0 text-xs font-semibold text-slate-500">{r.label}</div>
          <div className="flex-1 text-xs text-slate-700 leading-snug">{r.value}</div>
        </div>
      ))}
    </div>
  );
}

function MetarKlartext({ raw }: { raw: string | null }) {
  if (!raw) return null;
  try {
    const rows = decodeMetar(raw);
    if (rows.length === 0) return <Raw raw={raw} />;
    return <Rows rows={rows} />;
  } catch {
    return <Raw raw={raw} />;
  }
}

function TafKlartextView({ raw }: { raw: string | null }) {
  if (!raw) return null;
  let taf: TafKlartext;
  try {
    taf = decodeTaf(raw);
  } catch {
    return <Raw raw={raw} />;
  }
  return (
    <div>
      <div className="flex gap-2 items-start">
        <div className="w-28 shrink-0 text-xs font-semibold text-slate-500">Gültig</div>
        <div className="flex-1 text-xs text-slate-700 leading-snug">{taf.validity}</div>
      </div>
      {taf.base.length > 0 ? <div className="mt-1"><Rows rows={taf.base} /></div> : null}
      {taf.trends.map((tr, i) => (
        <div key={i} className="mt-2 pt-1.5 border-t border-slate-100">
          <div className="text-[11px] font-bold text-brand-600 mb-1">{tr.heading}</div>
          <Rows rows={tr.rows} />
        </div>
      ))}
    </div>
  );
}
